import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailConfigApi, type EmailConfigDto } from '../../api/emailConfig';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useToast } from '../../components/Toast';

export default function EmailConfig() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: config, isLoading } = useQuery({
    queryKey: ['email-config'],
    queryFn: emailConfigApi.get,
  });

  const saveMutation = useMutation({
    mutationFn: emailConfigApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-config'] });
      toast.addToast('Email configuration saved', 'success');
    },
    onError: (err: Error) => toast.addToast(`Error: ${err.message}`, 'error'),
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-xl font-bold text-gray-900">Email Configuration</h1>

      <EmailConfigForm config={config} onSave={(data) => saveMutation.mutate(data)} saving={saveMutation.isPending} />

      {config?.isConfigured && (
        <div className="bg-green-50 rounded-lg border border-green-200 p-3">
          <p className="text-sm text-green-800 font-medium">
            Email is configured and ready. Invoices can be sent from the Invoice Detail page.
          </p>
        </div>
      )}
    </div>
  );
}

function EmailConfigForm({ config, onSave, saving }: {
  config: EmailConfigDto | undefined;
  onSave: (data: { smtpHost?: string; smtpPort?: number; smtpUsername?: string; smtpPassword?: string; useSsl?: boolean; fromEmail?: string; fromName?: string; replyToEmail?: string }) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    smtpHost: config?.smtpHost ?? '',
    smtpPort: config?.smtpPort ?? 587,
    smtpUsername: config?.smtpUsername ?? '',
    smtpPassword: '',
    useSsl: config?.useSsl ?? true,
    fromEmail: config?.fromEmail ?? '',
    fromName: config?.fromName ?? '',
    replyToEmail: config?.replyToEmail ?? '',
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900 mb-3">SMTP Settings</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">SMTP Host *</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            placeholder="smtp.gmail.com"
            value={form.smtpHost} onChange={(e) => setForm({ ...form, smtpHost: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">SMTP Port</label>
          <input type="number" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.smtpPort} onChange={(e) => setForm({ ...form, smtpPort: parseInt(e.target.value) || 587 })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Use SSL</label>
          <div className="flex items-center h-[34px]">
            <input type="checkbox"
              checked={form.useSsl}
              onChange={(e) => setForm({ ...form, useSsl: e.target.checked })} />
            <span className="ml-2 text-sm text-gray-600">{form.useSsl ? 'Yes (TLS)' : 'No'}</span>
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">SMTP Username *</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.smtpUsername} onChange={(e) => setForm({ ...form, smtpUsername: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">SMTP Password {config?.isConfigured ? '(leave blank to keep)' : '*'}</label>
          <input type="password" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.smtpPassword} onChange={(e) => setForm({ ...form, smtpPassword: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">From Email *</label>
          <input type="email" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.fromEmail} onChange={(e) => setForm({ ...form, fromEmail: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">From Name</label>
          <input type="text" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.fromName} onChange={(e) => setForm({ ...form, fromName: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Reply-To Email</label>
          <input type="email" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            value={form.replyToEmail} onChange={(e) => setForm({ ...form, replyToEmail: e.target.value })} />
        </div>
      </div>
      <div className="mt-3">
        <button
          className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          onClick={() => onSave(form)}
          disabled={saving || !form.smtpHost || !form.smtpUsername || !form.fromEmail}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
}
